import {
  Box,
  Button,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Reorder } from "@mui/icons-material";
import { useState } from "react";
import PropTypes from "prop-types";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { DragIndicator } from "@mui/icons-material";
import { CSS } from "@dnd-kit/utilities";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  p: 4,
};

function ReorderModal({
  buttonTitle,
  title,
  questions,
  confirmLabel,
  cancelLabel,
  onConfirm,
  isDisabled,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(questions || []);

  const handleOpen = () => {
    setOpen(true);  
    setItems(questions || []);
  };
  const handleClose = () => {
    setOpen(false);
    setItems(questions || []);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function DraggableTableRow({ question, index }) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: question._id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      cursor: "grab",
      opacity: isDragging ? 0.7 : 1,
      borderRadius: 4,
      boxShadow: isDragging ? "0 0 10px rgba(0,0,0,0.8)" : "none",
    };

    return (
      <TableRow ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <TableCell>{index}</TableCell>
        <TableCell>{question.question}</TableCell>
        <TableCell>
          <DragIndicator />
        </TableCell>
      </TableRow>
    );
  }

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      setItems((items) => {
        const oldPosition = items.findIndex((item) => item._id === active.id);
        const newPosition = items.findIndex((item) => item._id === over.id);

        return arrayMove(items, oldPosition, newPosition);
      });
    }
  };

  const handleConfirm = async () => {
    try {
      const reorderedQuestions = items.map((item, index) => ({
        _id: item._id,
        order: index + 1,
      }));

      console.log("Reordered Questions:", reorderedQuestions);

      await onConfirm?.(reorderedQuestions);
      handleClose();
    } catch (error) {
      console.error("Failed to confirm archive action:", error);
    }
    handleClose();
  };

  return (
    <>
      <Button
        variant="contained"
        color="info"
        sx={{ placeSelf: "center" }}
        endIcon={<Reorder />}
        disabled={isDisabled}
        onClick={handleOpen}
      >
        {buttonTitle ? buttonTitle : "Reorder Questions"}
      </Button>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            sx={{ fontWeight: "bold" }}
          >
            {title}
          </Typography>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Questions</TableCell>
                <TableCell>Drag</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={items.map((item) => item._id)}
                  strategy={verticalListSortingStrategy}
                >
                  {/* <List> */}
                  {items.map((question, index) => (
                    <DraggableTableRow
                      key={question._id}
                      question={question}
                      index={index + 1}
                    />
                  ))}
                  {/* </List> */}
                </SortableContext>
              </DndContext>
            </TableBody>
          </Table>

          <Box
            sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}
          >
            <Button variant="contained" color="primary" onClick={handleConfirm}>
              {confirmLabel}
            </Button>
            <Button variant="outlined" color="secondary" onClick={handleClose}>
              {cancelLabel}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

ReorderModal.propTypes = {
  buttonTitle: PropTypes.string,
  title: PropTypes.string,
  questions: PropTypes.array,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  onConfirm: PropTypes.func,
  isDisabled: PropTypes.bool,
};

export default ReorderModal;
